import { memo, useCallback, useEffect, useRef } from 'react';

import clsx from 'clsx';
import { useRouter } from 'next/router';

import MainMenuNav from '~/features/navigation/components/N20MainMenu/components/MainMenuNav';
import { isSSR } from '~/shared/utils/platform';

import { useLayoutScroll } from './../../hooks/useLayoutScroll';
import MainMenuHotLinks from './components/MainMenuHotLinks/MainMenuHotLinks';
import MainMenuLogo from './components/MainMenuLogo/MainMenuLogo';
import MainMenuToggle from './components/MainMenuToggle/MainMenuToggle';
import IMainMenu from './N20MainMenu.def';
import styles from './n20MainMenu.module.css';
import useMainMenuState from './useMainMenuState';

import { N21MetaHeader } from '../N21MetaHeader/N21MetaHeader';

export const COLLAPSE_EVENT_NAME = 'rdg:collapseMenu';

export const triggerMenuCollapseOnElement = (targetElement?: HTMLElement) => {
	targetElement?.querySelectorAll('[data-submenu]').forEach((accordion) => {
		const event = new CustomEvent(COLLAPSE_EVENT_NAME, {
			bubbles: false,
		});
		accordion.dispatchEvent(event);
	});
};

const N20MainMenu = memo(({ menuNodes, hasMetaHeader, currentVertical, metaMenu }: IMainMenu) => {
	const router = useRouter();
	const headerRef = useRef<HTMLElement>(null);
	const { menuIsOpen, activeItemSecondary } = useMainMenuState((state) => ({
		menuIsOpen: state.menuIsOpen,
		activeItemSecondary: state.activeItemSecondary,
	}));
	const { setActiveItemMain, clearMainMenuStates } = useMainMenuState((state) => ({
		setActiveItemMain: state.setActiveItemMain,
		clearMainMenuStates: state.clearMainMenuStates,
	}));

	useLayoutScroll(!menuIsOpen);

	const handleMouseLeave = () => {
		if (menuIsOpen || activeItemSecondary !== null) {
			return;
		}

		setActiveItemMain(null);
	};

	const resetMenu = useCallback(() => {
		clearMainMenuStates();
		triggerMenuCollapseOnElement(headerRef.current || undefined);
	}, [clearMainMenuStates]);

	const handleRouteChange = useCallback(() => {
		if (window.sessionStorage.getItem('navigationMenuKeepActive')) {
			window.sessionStorage.removeItem('navigationMenuKeepActive');
		} else {
			resetMenu();
		}
	}, [resetMenu]);

	useEffect(() => {
		if (isSSR) {
			return;
		}

		router.events.on('routeChangeStart', handleRouteChange);

		return () => {
			router.events.off('routeChangeStart', handleRouteChange);
		};
	}, [handleRouteChange]);

	return (
		<>
			<button
				className={clsx(styles.openMenuOverlay, { [styles.open]: menuIsOpen })}
				aria-hidden
				onClick={resetMenu}
			/>

			<header
				className={clsx('n20-main-menu', styles.root, { [styles.hasMetaHeader]: hasMetaHeader })}
				ref={headerRef}
				onMouseLeave={handleMouseLeave}
			>
				{metaMenu?.items?.length ? (
					<MemoizedMetaMenu
						items={metaMenu.items}
						moduleBackgroundColor={metaMenu.moduleBackgroundColor}
					/>
				) : null}
				<div className={styles.headerContent}>
					<div className={styles.menuToggleWrapper}>
						<MainMenuToggle />
					</div>

					<MainMenuLogo />

					<MainMenuNav
						nodes={menuNodes}
						hasMetaHeader={hasMetaHeader}
						currentVertical={currentVertical}
					/>

					<MainMenuHotLinks />
				</div>
			</header>
		</>
	);
});

export default N20MainMenu;

//memoize the meta menu items
const MemoizedMetaMenu = memo(N21MetaHeader);

export const NAVIGATION_MAIN_DEFAULT_SECONDARY_ID = 'defaultSecondary';
